import { getElement, getElements } from '../Utility/Dom'

const CONTENT_QUERY_PARAM = 'tx_solr[q]'
const COURSE_QUERY_PARAM = 'tx_sitepackage_coursesearch[q]'

class SearchModal {
  private activeButton: HTMLElement | null = null
  private readonly boundHandleBackdropClick = this.handleBackdropClick.bind(this)
  private readonly boundHandleClose = this.handleClose.bind(this)
  private readonly boundHandleKeyDown = this.handleKeyDown.bind(this)
  private readonly closeButton: HTMLElement | null
  private readonly contentCard: HTMLAnchorElement | null
  private readonly contentCardHref: string
  private readonly courseCard: HTMLAnchorElement | null
  private readonly courseCardHref: string
  private readonly dialog: HTMLDialogElement | null
  private readonly openButtons: NodeListOf<HTMLElement>

  constructor() {
    this.closeButton = getElement('search-modal-close')
    this.contentCard = getElement('search-switch-content') as HTMLAnchorElement | null
    this.courseCard = getElement('search-switch-course') as HTMLAnchorElement | null
    this.contentCardHref = this.contentCard?.href ?? ''
    this.courseCardHref = this.courseCard?.href ?? ''
    this.dialog = getElement('search-modal') as HTMLDialogElement | null
    this.openButtons = getElements('header-search-button')
  }

  public init(): void {
    if (this.dialog === null) return
    if (this.openButtons.length === 0) return

    this.addEvents()
  }

  private addEvents(): void {
    this.openButtons.forEach((button: HTMLElement): void => {
      button.addEventListener('click', (): void => this.handleOpen(button))
    })
    this.closeButton?.addEventListener('click', this.boundHandleClose)
    this.dialog?.addEventListener('click', this.boundHandleBackdropClick)
    this.dialog?.addEventListener('keydown', this.boundHandleKeyDown)
  }

  private applyTerm(card: HTMLAnchorElement | null, baseHref: string, param: string, term: string): void {
    if (card === null || baseHref === '') return

    const url = new URL(baseHref)
    if (term !== '') {
      url.searchParams.set(param, term)
    }
    card.href = url.toString()
  }

  // Carry the current search term across the audience switch so visitors keep
  // their query when moving between course and content search (VHWWEB-118).
  private carryOverTerm(): void {
    const term = this.currentTerm()
    this.applyTerm(this.courseCard, this.courseCardHref, COURSE_QUERY_PARAM, term)
    this.applyTerm(this.contentCard, this.contentCardHref, CONTENT_QUERY_PARAM, term)
  }

  private currentTerm(): string {
    const params = new URLSearchParams(window.location.search)

    return params.get(CONTENT_QUERY_PARAM) ?? params.get(COURSE_QUERY_PARAM) ?? ''
  }

  private handleBackdropClick(event: MouseEvent): void {
    if (event.target === this.dialog) {
      this.handleClose()
    }
  }

  private handleClose(): void {
    this.dialog?.close()
    this.activeButton?.setAttribute('aria-expanded', 'false')
    this.activeButton?.focus()
    this.activeButton = null
  }

  private handleKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      this.handleClose()
    }
  }

  private handleOpen(button: HTMLElement): void {
    this.activeButton = button
    this.carryOverTerm()
    this.dialog?.showModal()
    button.setAttribute('aria-expanded', 'true')
  }
}

const searchModal = new SearchModal()
searchModal.init()
